import _ from 'lodash';
import classnames from 'classnames';
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import connect from 'components/lib/connect';
import { containsVariables, decodeVariablePlaceholders } from 'components/text_editor/lib/variable_helpers';
import { getNormalizedUrl, isUrlValid } from 'components/lib/linkify_it';
import PopoverCard from 'components/common/popover_card';

const ESCAPE = 27;
const ENTER = 13;

class FloatingLinkInput extends React.Component {
  constructor(props) {
    super(props);
    _.bindAll(this, ['onKeyDown']);
  }

  componentDidMount() {
    // unfortunately needed for IE
    setImmediate(() => {
      this.input && this.input.focus();
    });
  }

  render() {
    return (
      <StyledInput
        className="draftStyles-floatingLinkMenu-input"
        data-aid="floatingLinkMenu-input"
        onBlur={this.props.onBlur}
        onChange={this.props.onChange}
        onKeyDown={this.onKeyDown}
        placeholder="http://www.example.com"
        ref={node => (this.input = node)}
        value={this.props.value}
      />
    );
  }
  onKeyDown(evt) {
    if (evt.keyCode === ENTER) {
      evt.preventDefault();
      this.props.onSubmit();
    } else if (evt.keyCode === ESCAPE) {
      evt.preventDefault();
      this.props.onEscape();
    }
  }
}

FloatingLinkInput.propTypes = {
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onEscape: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

export { FloatingLinkInput };

class SlateFloatingLinkMenu extends React.Component {
  constructor(props) {
    super(props);
    _.bindAll(this, [
      'onBlur',
      'onChange',
      'onEscape',
      'onSubmit',
      'onRemove',
      'onClickChange',
      'onInsertVariable',
      'toggleVariableMenu',
    ]);
    this.state = {
      isCurrentlyEditing: false,
      url: decodeVariablePlaceholders(this.getCurrentUrl()) || '',
      showVariableMenu: false,
    };
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    this.setTargetElement();
  }

  componentDidUpdate() {
    this.setTargetElement();
  }

  setTargetElement() {
    if (!this.state.targetElement) {
      const targetElement = window.document.querySelector(`[data-key="${this.props.id}"]`);
      if (targetElement) {
        this.setState({ targetElement });
      }
    }
  }

  render() {
    return (
      <StyledPopoverCard
        className="slate-floatingLinkMenu"
        data-aid="slate-floatingLinkMenu"
        isVisible
        position="top"
        targetElement={this.state.targetElement}
      >
        {this.getCurrentUrl() ? this.renderEditing() : this.renderAdding()}
      </StyledPopoverCard>
    );
  }

  renderVariableButton() {
    const { interpolateVariablesInURL, kbVariables } = this.props;
    if (!interpolateVariablesInURL || !kbVariables || kbVariables.length === 0) {
      return null;
    }

    return (
      <VariableButtonWrapper>
        <VariableButton
          onClick={this.toggleVariableMenu}
          onMouseDown={evt => evt.preventDefault()}
          title="Insert variable"
        >
          /var
        </VariableButton>
        {this.state.showVariableMenu && (
          <VariableDropdown>
            {kbVariables.map(variable => (
              <VariableItem
                key={variable.id}
                onClick={() => this.onInsertVariable(variable)}
                onMouseDown={evt => evt.preventDefault()}
              >
                {variable.name || variable.variableType || variable.type}
              </VariableItem>
            ))}
          </VariableDropdown>
        )}
      </VariableButtonWrapper>
    );
  }

  renderEditing() {
    if (this.state.isCurrentlyEditing) {
      return (
        <VariableMenuWrapper>
          <span className="draftStyles-floatingLinkMenu-wrapper">
            <FloatingLinkInput
              onBlur={this.onBlur}
              onChange={this.onChange}
              onEscape={this.onEscape}
              onSubmit={this.onSubmit}
              ref={this.inputRef}
              value={this.state.url}
            />
            <span className="draftStyles-floatingLinkMenu-separator" />
            {this.renderVariableButton()}
            <span
              className="draftStyles-floatingLinkMenu-save"
              data-aid="floatingLinkMenu-save"
              onClick={this.onSubmit}
              onMouseDown={evt => evt.preventDefault()}
            >
              Save
            </span>
            <span
              className="draftStyles-floatingLinkMenu-remove"
              onClick={this.onRemove}
              onMouseDown={evt => evt.preventDefault()}
            >
              Remove
            </span>
          </span>
        </VariableMenuWrapper>
      );
    }

    const url = this.getCurrentUrl();
    const linkClassNames = classnames('draftStyles-floatingLinkMenu-link', {
      'draftStyles-floatingLinkMenu-link-invalid': !isUrlValid(url),
    });

    return (
      <span className="draftStyles-floatingLinkMenu-wrapper" data-aid="floatingLinkMenu-wrapper">
        <a className={linkClassNames} href={url} rel="noreferrer" target="_blank">
          {url}
        </a>
        <span className="draftStyles-floatingLinkMenu-separator" />
        <span
          className="draftStyles-floatingLinkMenu-change"
          data-aid="floatingLinkMenu-change"
          onClick={this.onClickChange}
          onMouseDown={evt => evt.preventDefault()}
        >
          Change
        </span>
        <span
          className="draftStyles-floatingLinkMenu-remove"
          onClick={this.onRemove}
          onMouseDown={evt => evt.preventDefault()}
        >
          Remove
        </span>
      </span>
    );
  }

  renderAdding() {
    return (
      <VariableMenuWrapper>
        <span className="draftStyles-floatingLinkMenu-wrapper">
          <FloatingLinkInput
            onBlur={this.onBlur}
            onChange={this.onChange}
            onEscape={this.onEscape}
            onSubmit={this.onSubmit}
            ref={this.inputRef}
            value={this.state.url || ''}
          />
          <span className="draftStyles-floatingLinkMenu-separator" />
          {this.renderVariableButton()}
          <span
            className="draftStyles-floatingLinkMenu-add"
            data-aid="floatingLinkMenu-add"
            onClick={this.onSubmit}
            onMouseDown={evt => evt.preventDefault()}
          >
            Add
          </span>
        </span>
      </VariableMenuWrapper>
    );
  }

  // Helpers

  getCurrentUrl() {
    if (!this.props.editor) {
      return null;
    }

    const editor = this.props.editor;
    const value = editor.value;
    let url;
    value.inlines.forEach(inline => {
      if (inline.type === 'link') {
        url = inline.get('data').get('href');
      }
    });

    return decodeVariablePlaceholders(url);
  }

  // Handlers

  onBlur(evt) {
    if (!this.getCurrentUrl()) {
      this.onRemove();
    }
  }

  onChange(event) {
    const url = event.target.value;
    this.setState({ url });
  }

  onEscape() {
    this.getCurrentUrl() ? this.setState({ isCurrentlyEditing: false }) : this.onRemove();
  }

  onRemove() {
    this.props.onChange(this.props.editor.unwrapInline('link'));
  }

  onSubmit() {
    const editor = this.props.editor;
    const value = editor.value;
    let link;
    value &&
      value.inlines.forEach(inline => {
        if (inline.type === 'link') {
          link = inline;
        }
      });
    if (!link) return;

    let currentData = link.data.toJSON();

    // Don't normalize URL if it contains variable placeholders - preserve them as-is
    const href = containsVariables(this.state.url) ? this.state.url : getNormalizedUrl(this.state.url);

    let newData = { ...currentData, href };
    this.props.onChange(editor.setNodeByKey(link.key, { data: newData }).focus());
    this.setState({ url: href, isCurrentlyEditing: false });
  }

  onClickChange() {
    this.setState({ isCurrentlyEditing: true });
  }

  toggleVariableMenu() {
    this.setState({ showVariableMenu: !this.state.showVariableMenu });
  }

  onInsertVariable(variable) {
    const input = this.inputRef.current && this.inputRef.current.input;
    const value = this.state.url || '';

    // Get cursor position from input if available, otherwise append to end
    const start = input ? input.selectionStart || 0 : value.length;
    const end = input ? input.selectionEnd || 0 : value.length;

    // Insert {{.VARIABLE_TYPE}} format using the actual variable type
    const variableType = variable.variableType || variable.type;
    const variablePlaceholder = `{{.${variableType}}}`;
    const newValue = value.substring(0, start) + variablePlaceholder + value.substring(end);

    this.setState({ url: newValue, showVariableMenu: false }, () => {
      if (input) {
        const newCursorPos = start + variablePlaceholder.length;
        input.setSelectionRange(newCursorPos, newCursorPos);
        input.focus();
      }
    });
  }
}

SlateFloatingLinkMenu.propTypes = {
  editor: PropTypes.object,
  id: PropTypes.string,
  interpolateVariablesInURL: PropTypes.bool,
  kbVariables: PropTypes.array,
  onChange: PropTypes.func,
};

const StyledPopoverCard = styled(PopoverCard)`
  padding: 0;
`;

const StyledInput = styled.input`
  margin: 0;
  width: 350px !important;
`;

const VariableMenuWrapper = styled.div`
  position: relative;
`;

const VariableButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const VariableButton = styled.span`
  background-color: ${p => p.theme.colors.green500};
  color: ${p => p.theme.colors.white};
  border-radius: 3px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  margin-right: 8px;

  &:hover {
    background-color: ${p => p.theme.colors.green600};
  }
`;

const VariableDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background-color: ${p => p.theme.colors.white};
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  min-width: 200px;
  border: 1px solid ${p => p.theme.colors.gray300};
`;

const VariableItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: ${p => p.theme.colors.gray200};
  }
`;

export { StyledInput };

function mapStateToProps({ isFeatureEnabled }) {
  return {
    interpolateVariablesInURL: isFeatureEnabled('interpolateVariablesInURL'),
  };
}

export default connect(mapStateToProps)(SlateFloatingLinkMenu);
